import React, { useEffect, useMemo, useState } from 'react';
import styles from '../Styles/Baseinformation.module.css';
import IconHome from '../Images/IconHome';
import { useSelectedCompany } from '../Commoncontext/SelectedCompanyContext';
import { useToast } from '../Commoncontext/ToastContext';
import { getCompanyBaseInfo, updateCompanyBaseInfo } from '../Api/CompanyApi';

// ---- kenttävalidointi ----
function isValidYtunnus(v) {
  return /^\d{7}-\d$/.test(String(v || '').trim());
}
function isFiveDigitZip(v) {
  return /^\d{5}$/.test(String(v || '').trim());
}
function isYear(v) {
  if (v === '' || v === null || v === undefined) return true;
  const n = Number(v);
  return Number.isInteger(n) && n >= 1800 && n <= 2100;
}
function isNonNegInt(v) {
  if (v === '' || v === null || v === undefined) return true;
  const n = Number(v);
  return Number.isInteger(n) && n >= 0;
}
function isEmail(v) {
  if (!v) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim());
}
function isPhone(v) {
  if (!v) return true;
  return /^[+0-9\s\-()]{5,20}$/.test(String(v).trim());
}

export default function Baseinformation() {
  const { selectedCompanyId } = useSelectedCompany();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [errors, setErrors]   = useState({}); // {key: 'virhe'}

  const [form, setForm] = useState({
    // Perustiedot
    nimi: '',
    ytunnus: '',
    osoite: '',
    postinumero: '',
    postitoimipaikka: '',
    valmistumisvuosi: '',
    kaupparekisteripaiva: '',
    osakemaara: '',
    yjPaiva: '',
    // Rakenne & tekniikka
    porraskaytavia: '',
    kerroksia: '',
    huoneistoja: '',
    autopaikkoja: '',
    lammitus: '',
    ilmanvaihto: '',
    aurinkosahko: false,
    latausinfra: false,
    // Hallinto
    hallitus: [], // { id, rooli: 'pj'|'jasen', nimi, email, puhelin }
  });

  const handleChange = (field, value) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const updateMember = (idx, patch) =>
    setForm(prev => {
      const next = [...prev.hallitus];
      next[idx] = { ...next[idx], ...patch };
      return { ...prev, hallitus: next };
    });

  const addMember = () =>
    setForm(prev => ({
      ...prev,
      hallitus: [
        ...prev.hallitus,
        { id: `m-${Date.now()}`, rooli: 'jasen', nimi: '', email: '', puhelin: '' },
      ],
    }));

  const removeMember = (idx) =>
    setForm(prev => ({
      ...prev,
      hallitus: prev.hallitus.filter((_, i) => i !== idx),
    }));

  // ----- Haku Firebasesta -----
  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!selectedCompanyId) return;
      setLoading(true);
      try {
        const data = await getCompanyBaseInfo(selectedCompanyId);
        if (ignore) return;
        setForm({
          nimi: data?.nimi || '',
          ytunnus: data?.ytunnus || '',
          osoite: data?.osoite || '',
          postinumero: data?.postinumero || '',
          postitoimipaikka: data?.postitoimipaikka || '',
          valmistumisvuosi: data?.valmistumisvuosi ?? '',
          kaupparekisteripaiva: data?.kaupparekisteripaiva || '',
          osakemaara: data?.osakemaara ?? '',
          yjPaiva: data?.yjPaiva || '',
          porraskaytavia: data?.porraskaytavia ?? '',
          kerroksia: data?.kerroksia ?? '',
          huoneistoja: data?.huoneistoja ?? '',
          autopaikkoja: data?.autopaikkoja ?? '',
          lammitus: data?.lammitus || '',
          ilmanvaihto: data?.ilmanvaihto || '',
          aurinkosahko: !!data?.aurinkosahko,
          latausinfra: !!data?.latausinfra,
          hallitus: Array.isArray(data?.hallitus)
            ? data.hallitus.map(m => ({
                id: m?.id || `m-${Math.random().toString(36).slice(2)}`,
                rooli: ['pj','jasen'].includes(m?.rooli) ? m.rooli : 'jasen',
                nimi: m?.nimi || '',
                email: m?.email || '',
                puhelin: m?.puhelin || '',
              }))
            : [],
        });
      } catch {
        showToast('Tietojen haku epäonnistui.', 'error');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [selectedCompanyId, showToast]);

  // ----- Validointi -----
  function validate(f) {
    const errs = {};
    if (!String(f.nimi || '').trim()) errs.nimi = 'Pakollinen';
    if (!isValidYtunnus(f.ytunnus)) errs.ytunnus = 'Virheellinen Y-tunnus (1234567-8)';
    if (!String(f.osoite || '').trim()) errs.osoite = 'Pakollinen';
    if (!isFiveDigitZip(f.postinumero)) errs.postinumero = '5 numeroa';
    if (!String(f.postitoimipaikka || '').trim()) errs.postitoimipaikka = 'Pakollinen';

    if (!isYear(f.valmistumisvuosi)) errs.valmistumisvuosi = 'Virheellinen vuosi';
    if (!isNonNegInt(f.osakemaara)) errs.osakemaara = 'Anna kokonaisluku ≥ 0';

    (f.hallitus || []).forEach((m, i) => {
      if (m.email && !isEmail(m.email)) errs[`hallitus.${i}.email`] = 'Virheellinen sähköposti';
      if (m.puhelin && !isPhone(m.puhelin)) errs[`hallitus.${i}.puhelin`] = 'Virheellinen numero';
    });

    return errs;
  }
  const hasError = useMemo(() => (key) => !!errors[key], [errors]);
  const errMsg   = useMemo(() => (key) => errors[key] || '', [errors]);
  const inputClass = (key) => `${styles.input} ${hasError(key) ? styles.errorInput : ''}`;

  // ----- Tallenna -----
  async function onSave() {
    if (!selectedCompanyId) return;
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      showToast('Tarkista virheelliset kentät.', 'error');
      return;
    }
    setSaving(true);
    try {
      // siivotaan hallitus: jätä rivit, joissa on vähintään nimi/email/puhelin
      const hallitus = (form.hallitus || []).filter(m =>
        String(m.nimi || m.email || m.puhelin || '').trim() !== ''
      );
      await updateCompanyBaseInfo(selectedCompanyId, { ...form, hallitus });
      showToast('Perustiedot tallennettu.', 'success');
    } catch {
      showToast('Tallennus epäonnistui.', 'error');
    } finally {
      setSaving(false);
    }
  }

  function onCancel() {
    setErrors({});
    if (!selectedCompanyId) return;
    getCompanyBaseInfo(selectedCompanyId)
      .then(data => {
        setForm(prev => ({ ...prev, ...data }));
        showToast('Palautettu tallennettuihin tietoihin.', 'success');
      })
      .catch(() => showToast('Tietojen palautus epäonnistui.', 'error'));
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Otsikko */}
        <div className={styles.titleRow}>
          <div className={styles.titleLeft}>
            <IconHome />
            <h1>Taloyhtiön perustiedot</h1>
          </div>
          {loading && <div style={{color:'#bbb', fontSize:13}}>Ladataan…</div>}
        </div>

        {/* 2 korttia rinnakkain */}
        <div className={styles.topGrid}>
          {/* Perustiedot */}
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Perustiedot</h2>
            <div className={styles.grid2}>
              <div className={styles.inputBlock}>
                <label>Nimi *</label>
                <input
                  className={inputClass('nimi')}
                  value={form.nimi}
                  onChange={(e) => handleChange('nimi', e.target.value)}
                />
                {hasError('nimi') && <span className={styles.helpText}>{errMsg('nimi')}</span>}
              </div>
              <div className={styles.inputBlock}>
                <label>Y-tunnus *</label>
                <input
                  className={inputClass('ytunnus')}
                  placeholder="1234567-8"
                  value={form.ytunnus}
                  onChange={(e) => handleChange('ytunnus', e.target.value)}
                />
                {hasError('ytunnus') && <span className={styles.helpText}>{errMsg('ytunnus')}</span>}
              </div>

              <div className={styles.inputBlock}>
                <label>Osoite *</label>
                <input
                  className={inputClass('osoite')}
                  value={form.osoite}
                  onChange={(e) => handleChange('osoite', e.target.value)}
                />
                {hasError('osoite') && <span className={styles.helpText}>{errMsg('osoite')}</span>}
              </div>
              <div className={styles.inputBlock}>
                <label>Postinumero *</label>
                <input
                  className={inputClass('postinumero')}
                  value={form.postinumero}
                  onChange={(e) => handleChange('postinumero', e.target.value)}
                />
                {hasError('postinumero') && <span className={styles.helpText}>{errMsg('postinumero')}</span>}
              </div>

              <div className={styles.inputBlock}>
                <label>Postitoimipaikka *</label>
                <input
                  className={inputClass('postitoimipaikka')}
                  value={form.postitoimipaikka}
                  onChange={(e) => handleChange('postitoimipaikka', e.target.value)}
                />
                {hasError('postitoimipaikka') && <span className={styles.helpText}>{errMsg('postitoimipaikka')}</span>}
              </div>
              <div className={styles.inputBlock}>
                <label>Valmistumisvuosi</label>
                <input
                  className={inputClass('valmistumisvuosi')}
                  type="number"
                  value={form.valmistumisvuosi}
                  onChange={(e) => handleChange('valmistumisvuosi', e.target.value)}
                />
                {hasError('valmistumisvuosi') && <span className={styles.helpText}>{errMsg('valmistumisvuosi')}</span>}
              </div>

              <div className={styles.inputBlock}>
                <label>Kaupparekisterin merkintäpäivä</label>
                <input
                  className={styles.input}
                  type="date"
                  value={form.kaupparekisteripaiva}
                  onChange={(e) => handleChange('kaupparekisteripaiva', e.target.value)}
                />
              </div>
              <div className={styles.inputBlock}>
                <label>Osakemäärä</label>
                <input
                  className={inputClass('osakemaara')}
                  type="number"
                  value={form.osakemaara}
                  onChange={(e) => handleChange('osakemaara', e.target.value)}
                />
                {hasError('osakemaara') && <span className={styles.helpText}>{errMsg('osakemaara')}</span>}
              </div>

              <div className={styles.inputBlock}>
                <label>Yhtiöjärjestyksen päiväys</label>
                <input
                  className={styles.input}
                  type="date"
                  value={form.yjPaiva}
                  onChange={(e) => handleChange('yjPaiva', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Rakenne & tekniikka */}
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Kokotiedot ja talotekniikka</h2>
            <div className={styles.grid2}>
              <div className={styles.inputBlock}>
                <label>Porraskäytäviä</label>
                <input
                  className={styles.input}
                  type="number"
                  value={form.porraskaytavia}
                  onChange={(e) => handleChange('porraskaytavia', e.target.value)}
                />
              </div>
              <div className={styles.inputBlock}>
                <label>Kerroksia</label>
                <input
                  className={styles.input}
                  type="number"
                  value={form.kerroksia}
                  onChange={(e) => handleChange('kerroksia', e.target.value)}
                />
              </div>

              <div className={styles.inputBlock}>
                <label>Huoneistoja</label>
                <input
                  className={styles.input}
                  type="number"
                  value={form.huoneistoja}
                  onChange={(e) => handleChange('huoneistoja', e.target.value)}
                />
              </div>
              <div className={styles.inputBlock}>
                <label>Autopaikkoja</label>
                <input
                  className={styles.input}
                  type="number"
                  value={form.autopaikkoja}
                  onChange={(e) => handleChange('autopaikkoja', e.target.value)}
                />
              </div>

              <div className={styles.inputBlock}>
                <label>Lämmitys</label>
                <select
                  className={styles.input}
                  value={form.lammitus}
                  onChange={(e) => handleChange('lammitus', e.target.value)}
                >
                  <option value="">-</option>
                  <option value="Kaukolämpö">Kaukolämpö</option>
                  <option value="Maalämpö">Maalämpö</option>
                  <option value="Sähkölämmitys">Sähkölämmitys</option>
                </select>
              </div>
              <div className={styles.inputBlock}>
                <label>Ilmanvaihto</label>
                <select
                  className={styles.input}
                  value={form.ilmanvaihto}
                  onChange={(e) => handleChange('ilmanvaihto', e.target.value)}
                >
                  <option value="">-</option>
                  <option value="Koneellinen tulo/poisto">Koneellinen tulo/poisto</option>
                  <option value="Painovoimainen">Painovoimainen</option>
                </select>
              </div>

              <div className={styles.checkCell}>
                <label className={styles.checkLabel}>
                  <input
                    type="checkbox"
                    checked={form.aurinkosahko}
                    onChange={(e) => handleChange('aurinkosahko', e.target.checked)}
                  />
                  Aurinkosähkö
                </label>
              </div>
              <div className={styles.checkCell}>
                <label className={styles.checkLabel}>
                  <input
                    type="checkbox"
                    checked={form.latausinfra}
                    onChange={(e) => handleChange('latausinfra', e.target.checked)}
                  />
                  Latausinfrastruktuuri
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Hallinto – koko rivin leveydellä */}
        <div className={styles.cardWide}>
          <div className={styles.cardWideTop}>
            <h2 className={styles.cardTitle}>Hallinto</h2>
            <button type="button" className={styles.addBtn} onClick={addMember}>
              + Lisää jäsen
            </button>
          </div>

          <div className={styles.membersHeader}>
            <div>Rooli</div>
            <div>Nimi</div>
            <div>Sähköposti</div>
            <div>Puhelin</div>
            <div></div>
          </div>

          <div className={styles.membersList}>
            {form.hallitus.map((m, i) => (
              <div key={m.id ?? i} className={styles.memberRow}>
                <select
                  className={`${styles.input} ${styles.memberInput}`}
                  value={m.rooli || 'jasen'}
                  onChange={(e) => updateMember(i, { rooli: e.target.value })}
                >
                  <option value="pj">Hallituksen puheenjohtaja</option>
                  <option value="jasen">Hallituksen jäsen</option>
                </select>

                <input
                  className={`${styles.input} ${styles.memberInput}`}
                  placeholder="Nimi"
                  value={m.nimi || ''}
                  onChange={(e) => updateMember(i, { nimi: e.target.value })}
                />
                <input
                  className={`${styles.input} ${styles.memberInput} ${errors[`hallitus.${i}.email`] ? styles.errorInput : ''}`}
                  placeholder="Sähköposti"
                  value={m.email || ''}
                  onChange={(e) => updateMember(i, { email: e.target.value })}
                />
                <input
                  className={`${styles.input} ${styles.memberInput} ${errors[`hallitus.${i}.puhelin`] ? styles.errorInput : ''}`}
                  placeholder="Puhelin"
                  value={m.puhelin || ''}
                  onChange={(e) => updateMember(i, { puhelin: e.target.value })}
                />

                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={() => removeMember(i)}
                >
                  Poista
                </button>
              </div>
            ))}
            {form.hallitus.length === 0 && (
              <div className={styles.emptyHint}>Ei lisättyjä jäseniä.</div>
            )}
          </div>
        </div>

        {/* Alapainikkeet */}
        <div className={styles.actionsRow}>
          <button className={styles.saveBtn} type="button" onClick={onSave} disabled={saving}>
            Tallenna
          </button>
          <button className={styles.cancelBtn} type="button" onClick={onCancel} disabled={saving}>
            Peruuta
          </button>
        </div>
      </div>
    </div>
  );
}
